import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { toResult } from "#/modules/util.auth/api-errors";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { validatePassword } from "#/modules/util.auth/api.auth.server";
import { getAuthSession } from "#/modules/util.auth/session.auth.server";
import {
  updateAliasSchema,
  updateEmailSchema,
  updateNameSchema,
  updatePasswordSchema,
  updatePreferencesSchema,
} from "#/modules/user.account-settings/schema.account-settings";
import { toFieldErrors, translateApiError } from "#/modules/util.errors/translate";

/** GET /auth/user/info — the authenticated user's profile (name, email, twoFactorEnabled). */
export const getAccountUserInfo = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const result = toResult(await client.GET("/auth/user/info"));
    if (Result.isError(result)) throw result.error;

    const userInfo = result.value;
    if (!userInfo) throw new Error("Failed to load user info");

    return userInfo;
  });

/** POST /auth/user/info — update first/last name. */
export const submitUpdateName = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(updateNameSchema)
  .handler(async ({ data }) => {
    const result = toResult(
      await client.PUT("/auth/user/info", {
        body: { firstName: data.firstName, lastName: data.lastName },
      }),
    );

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

/** PUT /user/preferences — update docent voorkeuren (standaard aantal groepjes, null = geen; avatars in nieuwe lessen). */
export const submitUpdatePreferences = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(updatePreferencesSchema)
  .handler(async ({ data }) => {
    const result = toResult(
      await client.PUT("/user/preferences", {
        body: {
          defaultAantalGroepen: data.defaultAantalGroepen,
          defaultAvatarsEnabled: data.defaultAvatarsEnabled,
        },
      }),
    );

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }
  });

/** PUT /user/alias — update the naam die leerlingen zien (leeg = volledige naam). */
export const submitUpdateAlias = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(updateAliasSchema)
  .handler(async ({ data }) => {
    const result = toResult(
      await client.PUT("/user/alias", {
        body: { alias: data.alias === "" ? null : data.alias },
      }),
    );

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }
  });

/** POST /auth/user/email — request an email change (backend sends confirmation). */
export const submitUpdateEmail = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(updateEmailSchema)
  .handler(async ({ data }) => {
    const result = toResult(
      await client.PUT("/auth/user/email", {
        body: { email: data.email },
      }),
    );

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

/**
 * POST /auth/user/password — change password. Mirrors portal's action which validates the new
 * password against the backend policy before committing (surfaced as a `newPassword` field error).
 */
export const submitUpdatePassword = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(updatePasswordSchema)
  .handler(async ({ data }) => {
    const passwordValidation = await validatePassword(data.newPassword);
    if (passwordValidation) {
      return { fieldErrors: { newPassword: [passwordValidation.formError] } };
    }

    const result = toResult(
      await client.PUT("/auth/user/password", {
        body: { oldPassword: data.oldPassword, newPassword: data.newPassword },
      }),
    );

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }

    // The backend revokes every refresh token on a password change; adopt the fresh pair it
    // returns or this session dies at the next rotation too.
    if (result.value?.accessToken && result.value.refreshToken) {
      const session = await getAuthSession();
      await session.update({ tokens: result.value });
    }
  });
